import { _ as _defineProperty } from '../../unsorted/defineProperty.js';
import { _ as _objectWithoutProperties } from '../../unsorted/objectWithoutProperties.js';
import { Children, useMemo } from 'react';
import { m as merge } from '../../unsorted/index.js';
import { f as flattenChildren } from '../../unsorted/index3.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { useTheme as useTheme$1 } from '@emotion/react';
import { B as Body } from '../Body/Body.js';
import { E as EllipsizedText } from '../EllipsizedText/EllipsizedText.js';
import { S as SmartClone } from '../SmartClone/SmartClone.js';
import { useTheme } from '../../hooks/useTheme.js';
import { isChildSubcomponent } from '../../utils/subcomponentHelpers.js';
import { I as Icon } from '../Icon/Icon.js';
import { B as ButtCon, a as Button } from '../ButtCon/ButtCon.js';
import { B as Box } from '../Box/Box.js';
import { useForwardLocalDomRef } from '../../hooks/useForwardLocalDomRef.js';
import { useGetSubcomponentChild } from '../../hooks/useGetSubcompnentChild.js';
import { useSplitApartChildrenAndSubComponents } from '../../hooks/useSplitApartChildrenAndSubComponents.js';
import { useResizeObserver } from '../../hooks/useResizeObserver.js';

var bannerSx = {
  d: "flex",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  p: "base.spacing.x4",
  columnGap: "base.spacing.x2",
  rowGap: "base.spacing.x4",
  pos: "relative",
  boxShadow: "base.shadow.300",
  brad: "base.borderRadius.x6",
  minh: "base.spacing.x16",
  w: "100%"
};
var rightHandButtonsContainerSx = {
  d: "flex",
  gap: "base.spacing.x2",
  ml: "auto"
};
var getBannerColoring = function getBannerColoring(variant) {
  switch (variant) {
    case "success":
    case "fatal":
    case "guidance":
    case "attention":
      return {
        bg: "base.color.status.".concat(variant, ".bright")
      };
    case "standard":
    default:
      return {
        bg: "base.color.neutral.500"
      };
  }
};
var shouldInvertButtonColoring = function shouldInvertButtonColoring(bannerVariant, _ref, buttonVariant) {
  var base = _ref.base;
  if (bannerVariant === "fatal" && base.colorMode === "onLight" && buttonVariant === "tertiary") {
    return true;
  }
  var isDarkMode = base.colorMode === "onDark";
  var isStandardVariant = bannerVariant === "standard";
  var invertButtonColoring = isDarkMode && !isStandardVariant;
  return invertButtonColoring;
};
function shouldInvertTextColoring(variant, _ref2) {
  var base = _ref2.base;
  var isDarkMode = base.colorMode === "onDark";
  return variant === "fatal" || isDarkMode && variant !== "standard";
}

var DEFAULT_BANNER_VARIANT = "standard";

var _excluded$5 = ["children", "size", "weight", "variant", "className", "sx"];
function ownKeys$5(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$5(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$5(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$5(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function BannerCaption(_ref) {
  var children = _ref.children,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? "medium" : _ref$size,
    weight = _ref.weight,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_BANNER_VARIANT : _ref$variant,
    className = _ref.className,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$5);
  var theme = useTheme();
  var invertColoring = shouldInvertTextColoring(variant, theme);
  var mergedSx = merge({
    c: invertColoring ? "base.color.text.body.inverse.primary" : "base.color.text.body.primary"
  }, sx);
  var flattenedChildren = flattenChildren(children);
  return jsx(Body, _objectSpread$5(_objectSpread$5({}, props), {}, {
    size: size,
    weight: weight,
    sx: mergedSx,
    className: "".concat(className !== null && className !== void 0 ? className : "", " BannerCaption"),
    children: Children.map(flattenedChildren, function (child) {
      var childIsBiomeTextComponent = isChildSubcomponent(child, Body) || isChildSubcomponent(child, EllipsizedText);
      if (childIsBiomeTextComponent) {
        var _child$props$size, _child$props$weight;
        return jsx(SmartClone, {
          size: (_child$props$size = child.props.size) !== null && _child$props$size !== void 0 ? _child$props$size : size,
          weight: (_child$props$weight = child.props.weight) !== null && _child$props$weight !== void 0 ? _child$props$weight : weight,
          children: child
        });
      }
      return child;
    })
  }));
}
BannerCaption.displayName = "Banner.Caption";

var _excluded$4 = ["sx", "variant", "iconVariant", "icon", "className"];
function ownKeys$4(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$4(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$4(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$4(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function BannerIcon(_ref) {
  var _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_BANNER_VARIANT : _ref$variant,
    iconVariant = _ref.iconVariant,
    icon = _ref.icon,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded$4);
  var theme = useTheme$1();
  var invertColoring = shouldInvertTextColoring(variant, theme);
  return jsx(Icon, _objectSpread$4(_objectSpread$4({}, props), {}, {
    icon: icon,
    variant: iconVariant,
    className: "".concat(className !== null && className !== void 0 ? className : "", " BannerIcon"),
    sx: merge({
      position: "absolute",
      top: "base.spacing.x4",
      left: "base.spacing.x4",
      width: "base.icon.size.300",
      fill: invertColoring ? "base.color.text.body.inverse.primary" : "base.color.text.body.primary"
    }, sx)
  }));
}
BannerIcon.displayName = "Banner.Icon";

var _excluded$3 = ["variant", "bannerVariant", "className"];
function ownKeys$3(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$3(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$3(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$3(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function BannerRightButtCon(_ref) {
  var _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "tertiary" : _ref$variant,
    _ref$bannerVariant = _ref.bannerVariant,
    bannerVariant = _ref$bannerVariant === void 0 ? DEFAULT_BANNER_VARIANT : _ref$bannerVariant,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded$3);
  var theme = useTheme();
  var invertButtonColoring = shouldInvertButtonColoring(bannerVariant, theme, variant);
  return jsx(ButtCon, _objectSpread$3(_objectSpread$3({}, props), {}, {
    size: "small",
    variant: invertButtonColoring ? "".concat(variant, "/inverse") : variant,
    className: "".concat(className !== null && className !== void 0 ? className : "", " BannerRightButtCon")
  }));
}
BannerRightButtCon.displayName = "Banner.RightButtCon";

var _excluded$2 = ["variant", "bannerVariant", "className"];
function ownKeys$2(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$2(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$2(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$2(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function BannerRightButton(_ref) {
  var _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? "tertiary" : _ref$variant,
    _ref$bannerVariant = _ref.bannerVariant,
    bannerVariant = _ref$bannerVariant === void 0 ? DEFAULT_BANNER_VARIANT : _ref$bannerVariant,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded$2);
  var theme = useTheme();
  var invertButtonColoring = shouldInvertButtonColoring(bannerVariant, theme, variant);
  return jsx(Button, _objectSpread$2(_objectSpread$2({}, props), {}, {
    size: "small",
    variant: invertButtonColoring ? "".concat(variant, "/inverse") : variant,
    className: "".concat(className !== null && className !== void 0 ? className : "", " BannerRightButton")
  }));
}
BannerRightButton.displayName = "Banner.RightButton";

var _excluded$1 = ["children", "size", "weight", "variant", "className", "sx"];
function ownKeys$1(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread$1(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys$1(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys$1(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function BannerTitle(_ref) {
  var children = _ref.children,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? "medium" : _ref$size,
    _ref$weight = _ref.weight,
    weight = _ref$weight === void 0 ? "bold" : _ref$weight,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_BANNER_VARIANT : _ref$variant,
    className = _ref.className,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var theme = useTheme();
  var invertColoring = shouldInvertTextColoring(variant, theme);
  var mergedSx = merge({
    color: invertColoring ? "base.color.text.body.inverse.primary" : "base.color.text.body.primary"
  }, sx);
  var flattenedChildren = flattenChildren(children);
  return jsx(Body, _objectSpread$1(_objectSpread$1({}, props), {}, {
    size: size,
    weight: weight,
    sx: mergedSx,
    className: "".concat(className !== null && className !== void 0 ? className : "", " BannerTitle"),
    children: Children.map(flattenedChildren, function (child) {
      var childIsBiomeTextComponent = isChildSubcomponent(child, Body) || isChildSubcomponent(child, EllipsizedText);
      if (childIsBiomeTextComponent) {
        var _child$props$size, _child$props$weight;
        return jsx(SmartClone, {
          size: (_child$props$size = child.props.size) !== null && _child$props$size !== void 0 ? _child$props$size : size,
          weight: (_child$props$weight = child.props.weight) !== null && _child$props$weight !== void 0 ? _child$props$weight : weight,
          children: child
        });
      }
      return child;
    })
  }));
}
BannerTitle.displayName = "Banner.Title";

var _excluded = ["children", "variant", "sx", "testId", "forceMultilineText", "domRef", "className"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function Banner(_ref) {
  var _theme$components2;
  var children = _ref.children,
    _ref$variant = _ref.variant,
    variant = _ref$variant === void 0 ? DEFAULT_BANNER_VARIANT : _ref$variant,
    _ref$sx = _ref.sx,
    sx = _ref$sx === void 0 ? {} : _ref$sx,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? "banner" : _ref$testId,
    forceMultilineText = _ref.forceMultilineText,
    _ref$domRef = _ref.domRef,
    domRef = _ref$domRef === void 0 ? {
      current: null
    } : _ref$domRef,
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var localDomRef = useForwardLocalDomRef(domRef);
  var icon = useGetSubcomponentChild(children, BannerIcon);
  var title = useGetSubcomponentChild(children, BannerTitle);
  var caption = useGetSubcomponentChild(children, BannerCaption);
  var _useSplitApartChildre = useSplitApartChildrenAndSubComponents(children, [BannerIcon, BannerTitle, BannerCaption]),
    otherChildren = _useSplitApartChildre.otherChildren;
  var hasIcon = useMemo(function () {
    return Boolean(icon);
  }, [icon]);
  var mergedSx = useMemo(function () {
    var _theme$components$Ban, _theme$components;
    return merge(bannerSx, _objectSpread(_objectSpread({}, hasIcon ? {
      paddingLeft: "base.spacing.x14"
    } : {}), {}, {
      alignItems: forceMultilineText ? "flex-start" : "center"
    }), getBannerColoring(variant), (_theme$components$Ban = (_theme$components = theme.components) === null || _theme$components === void 0 || (_theme$components = _theme$components.Banner) === null || _theme$components === void 0 ? void 0 : _theme$components.sxOverride) !== null && _theme$components$Ban !== void 0 ? _theme$components$Ban : {}, sx);
  }, [sx, (_theme$components2 = theme.components) === null || _theme$components2 === void 0 || (_theme$components2 = _theme$components2.Banner) === null || _theme$components2 === void 0 ? void 0 : _theme$components2.sxOverride, variant, hasIcon, forceMultilineText]);
  var _useResizeObserver = useResizeObserver(localDomRef),
    height = _useResizeObserver.height;
  var iconTopMargin = {
    top: height > 64 ? "16px" : "19px"
  };
  return jsxs(Box, _objectSpread(_objectSpread({}, props), {}, {
    sx: mergedSx,
    testId: testId,
    domRef: localDomRef,
    className: "".concat(className !== null && className !== void 0 ? className : "", " Banner Banner--").concat(variant),
    children: [icon && jsx(SmartClone, {
      variant: variant,
      testId: icon.props.testId ? icon.props.testId : "".concat(testId, "__icon"),
      sx: icon.props.sx ? merge(iconTopMargin, icon.props.sx) : iconTopMargin,
      children: icon
    }), jsxs(Box, {
      sx: _objectSpread({
        d: "flex"
      }, forceMultilineText ? {
        flexDirection: "column",
        flex: 1
      } : {
        flexWrap: "wrap",
        columnGap: "base.spacing.x3"
      }),
      className: "innerTextContainer",
      children: [jsx(SmartClone, {
        variant: variant,
        testId: "".concat(testId, "__title"),
        children: title
      }), jsx(SmartClone, {
        variant: variant,
        testId: "".concat(testId, "__caption"),
        children: caption
      })]
    }), otherChildren || Array.isArray(otherChildren) && otherChildren.length ? jsx(Box, {
      className: "rightContainer",
      testId: "".concat(testId, "__rightContainer"),
      sx: _objectSpread(_objectSpread({}, rightHandButtonsContainerSx), forceMultilineText ? {
        alignSelf: "flex-start",
        flexDirection: "column"
      } : {}),
      children: Children.map(otherChildren, function (child) {
        var isButtConSubcomponent = isChildSubcomponent(child, BannerRightButtCon);
        return isChildSubcomponent(child, BannerRightButton) || isButtConSubcomponent ? jsx(SmartClone, {
          bannerVariant: variant,
          testId: isButtConSubcomponent ? "".concat(testId, "__rightButtCon") : "".concat(testId, "__rightButton"),
          children: child
        }) : child;
      })
    }) : null]
  }));
}
Banner.displayName = "Banner";
Banner.Icon = BannerIcon;
Banner.Title = BannerTitle;
Banner.Caption = BannerCaption;
Banner.RightButton = BannerRightButton;
Banner.RightButtCon = BannerRightButtCon;

export { Banner as B, DEFAULT_BANNER_VARIANT as D };
//# sourceMappingURL=Banner.js.map
